import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle, ArrowRight, FileText, Sparkles, User, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../lib/utils';
import ReactMarkdown from 'react-markdown';
import DataVisualization from './DataVisualization';
import DataTable from './DataTable';

const RenderExpandableSection = ({ 
  title, 
  icon: Icon, 
  isExpanded, 
  onToggle, 
  children,
  preview 
}) => (
  <div className="rounded-xl bg-gray-50/50 dark:bg-gray-800/50 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-gray-100/50 
        dark:hover:bg-gray-700/50 transition-colors duration-200"
    >
      <div className="flex items-center space-x-2">
        <Icon className="w-5 h-5 text-blue-500 dark:text-blue-400" />
        <span className="font-medium text-gray-700 dark:text-gray-300">
          {title}
        </span>
      </div>
      {isExpanded ? (
        <ChevronUp className="w-4 h-4 text-gray-500" />
      ) : (
        <ChevronDown className="w-4 h-4 text-gray-500" />
      )}
    </button>
    
    {!isExpanded && preview && (
      <div className="px-4 pb-4 opacity-60">
        {preview}
      </div>
    )}
    
    {isExpanded && (
      <div className="p-4 border-t border-gray-200/50 dark:border-gray-700/50
        bg-white/50 dark:bg-gray-900/50">
        {children}
      </div>
    )}
  </div>
);

const MessageComponent = React.memo(({ message, usedTurbo, onCitationClick }) => {
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(false);
  const [isVisualizationExpanded, setIsVisualizationExpanded] = useState(false);
  const [showSources, setShowSources] = useState(false);

  // Memoize the message data processing
  const messageData = useMemo(() => ({
    hasTableData: message.table_data?.rows?.length > 0,
    hasVisualization: !!message.visualization,
    hasAnalysis: message.analysis || message.reasoning_steps?.length > 0 || message.table_data?.analysis,
    content: message.content,
    hasPreviewPoints: message.preview_points?.length > 0,
    hasCitations: message.type !== 'spreadsheet' && (message.citations?.length > 0 || message.table_data?.citations?.length > 0)
  }), [message]);

  // Memoize the analysis content
  const analysisContent = useMemo(() => {
    if (message.content && (
      message.content.includes('Key findings and patterns:') ||
      message.content.includes('Notable statistics:') ||
      message.content.includes('Business insights:')
    )) {
      const lines = message.content.split('\n').filter(line => line.trim());
      return {
        preview: lines[0].replace(/^[0-9]+\.\s*\*\*(.*?)\*\*/, '$1').replace('Key findings and patterns:', '').trim(),
        hasMore: lines.length > 1,
        content: lines,
        shouldHideMainContent: true,
        isMarkdown: true
      };
    } else if (message.reasoning_steps?.length > 0) {
      return {
        preview: message.reasoning_steps[0],
        hasMore: message.reasoning_steps.length > 1,
        content: message.reasoning_steps,
        shouldHideMainContent: false
      };
    } else if (message.table_data?.analysis) {
      const lines = message.table_data.analysis.split('\n').filter(line => line.trim());
      return {
        preview: lines[0],
        hasMore: lines.length > 1,
        content: lines,
        shouldHideMainContent: false
      };
    } else if (message.analysis) {
      const lines = message.analysis.split('\n').filter(line => line.trim());
      return {
        preview: lines[0],
        hasMore: lines.length > 1,
        content: lines,
        shouldHideMainContent: false
      };
    }
    return null;
  }, [message.content, message.reasoning_steps, message.table_data?.analysis, message.analysis]);

  // Memoize the citation click handler
  const handleCitationClick = useCallback((citation) => {
    if (!citation?.text) {
      console.warn('Invalid citation data');
      return;
    }
    onCitationClick?.(citation);
  }, [onCitationClick]);

  // Combine citations from both message and table data
  const allCitations = useMemo(() => {
    if (message.type === 'spreadsheet') {
      return [];
    }
    const messageCitations = message.citations || [];
    const tableCitations = message.table_data?.citations || [];
    return [...messageCitations, ...tableCitations];
  }, [message.citations, message.table_data?.citations, message.type]);

  // Development logging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Message component received:', { 
        table_data: message.table_data,
        hasRows: message.table_data?.rows?.length > 0,
        metadata: message.table_data?.metadata 
      });
    }
  }, [message.table_data]);

  return (
    <div className={cn(
      "flex mb-4 animate-slideIn",
      message.role === 'user' ? 'justify-end w-full' : 'justify-start'
    )}>
      <div className={cn(
        "rounded-2xl shadow-lg backdrop-blur-sm transition-all duration-300",
        message.role === 'user'
          ? "max-w-[85%] bg-gradient-to-br from-blue-600 to-purple-600 text-white p-4 hover:shadow-xl"
          : "w-[95%] bg-white/90 dark:bg-gray-900/90 text-gray-900 dark:text-gray-100 border border-gray-200/30 dark:border-gray-700/30 p-6 hover:shadow-md",
        message.type === 'error' && 'bg-red-50/90 dark:bg-red-900/50 border-red-200/30 dark:border-red-800/30',
        message.type === 'summary' && 'bg-gradient-to-br from-purple-50/90 to-blue-50/90 dark:from-purple-900/50 dark:to-blue-900/50'
      )}>
        <div className="space-y-4">
          {/* Table Data */}
          {messageData.hasTableData && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Query Results ({message.table_data.metadata?.totalRows} rows)
              </div>
              <div className="overflow-hidden rounded-lg border border-gray-200/50 dark:border-gray-700/50">
                <DataTable 
                  data={message.table_data} 
                  onError={(error) => console.error('DataTable error:', error)}
                />
              </div>
            </div>
          )}

          {/* Analysis Section - Updated to handle all analysis types */}
          {analysisContent && (
            <RenderExpandableSection
              title="AI Analysis"
              icon={Sparkles}
              isExpanded={isAnalysisExpanded}
              onToggle={() => setIsAnalysisExpanded(!isAnalysisExpanded)}
              preview={
                <div className="text-sm text-gray-600 dark:text-gray-300 bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
                  {analysisContent.isMarkdown ? (
                    <ReactMarkdown>{analysisContent.preview}</ReactMarkdown>
                  ) : (
                    analysisContent.preview
                  )}
                </div>
              }
            >
              <div className="space-y-3">
                {analysisContent.content.map((step, index) => (
                  <div key={index} className={cn(
                    "flex items-start gap-2",
                    !analysisContent.isMarkdown && "flex items-start gap-2"
                  )}>
                    {!analysisContent.isMarkdown && (
                      <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                    )}
                    <div className={cn(
                      "prose dark:prose-invert",
                      analysisContent.isMarkdown ? "max-w-none" : "flex-1"
                    )}>
                      {analysisContent.isMarkdown ? (
                        <ReactMarkdown>{step}</ReactMarkdown>
                      ) : (
                        <p className="text-gray-700 dark:text-gray-300">{step}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </RenderExpandableSection>
          )}

          {/* Message Content - Only show if it's not part of analysis */}
          {message.content && !analysisContent?.shouldHideMainContent && (
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          )}

          {/* Visualization Section */}
          {messageData.hasVisualization && (
            <RenderExpandableSection
              title="Data Visualization"
              icon={ChartIcon}
              isExpanded={isVisualizationExpanded}
              onToggle={() => setIsVisualizationExpanded(!isVisualizationExpanded)}
              preview={
                <div className="h-32 bg-white/50 dark:bg-gray-800/50 rounded-lg overflow-hidden">
                  <DataVisualization 
                    visualization={message.visualization}
                    fullSize={false}
                  />
                </div>
              }
            >
              <div className="pt-2 bg-white/50 dark:bg-gray-800/50 rounded-xl p-4">
                <DataVisualization 
                  visualization={message.visualization}
                  fullSize={true}
                />
              </div>
            </RenderExpandableSection>
          )}

          {/* Citations */}
          {messageData.hasCitations && (
            <div className="mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  SOURCES
                </p>
                <button
                  onClick={() => setShowSources(!showSources)}
                  className="flex items-center space-x-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <span>{showSources ? 'Hide' : 'Show'} ({allCitations.length})</span>
                  {showSources ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>
              
              {showSources && (
                <div className="space-y-3 animate-in fade-in-50">
                  {allCitations.map((citation, index) => (
                    <button
                      key={`${citation.document_id}-${index}`}
                      onClick={() => handleCitationClick(citation)}
                      className="block w-full group transform transition-all duration-300 hover:-translate-y-0.5"
                    >
                      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br 
                        from-blue-50/80 to-purple-50/80 dark:from-blue-900/20 dark:to-purple-900/20 
                        backdrop-blur-sm border border-blue-100/50 dark:border-blue-800/50
                        p-3 hover:shadow-md">
                        
                        {/* Citation header */}
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-300">
                              {citation.document_name || 'Document'} • Page {citation.page}
                            </span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-blue-500 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        
                        {/* Citation text */}
                        <div className="text-sm text-gray-600 dark:text-gray-300 leading-snug pl-5">
                          <span className="opacity-90">"{formatCitationText(citation.text)}"</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.table_data?.rows?.length === nextProps.message.table_data?.rows?.length &&
    prevProps.message.visualization === nextProps.message.visualization &&
    prevProps.message.citations?.length === nextProps.message.citations?.length &&
    prevProps.message.table_data?.citations?.length === nextProps.message.table_data?.citations?.length &&
    prevProps.usedTurbo === nextProps.usedTurbo
  );
});

// Utility functions and components
const formatCitationText = (text) => {
  if (!text) return '';
  const cleaned = text.replace(/\s+/g, ' ').trim();
  return cleaned.length > 150 ? `${cleaned.substring(0, 150)}...` : cleaned;
};

const ChartIcon = (props) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 3v18h18" />
    <path d="M7 12l4-4 4 4 4-4" />
  </svg>
);

export default MessageComponent;